<template>
  <div class="container mt-4">
    <div class="page-header">
      <div class="page-header__titles">
        <span class="page-header__accent"></span>
      <div>
        <h2 class="page-header__title">Directory</h2>
        <p class="page-header__subtitle">Discover, review, and manage members.</p>
      </div>
      </div>
    </div>
    <FiltersForm v-model="filters" :show-toggles="false" @apply="onApply" @reset="refresh">
      <!-- Search by company name -->
      <div class="filters__search">
        <input v-model="filters.name" type="text" class="filters__search-input"
          placeholder="Search by company name..." />
      </div>
      <div class="filters__search">
        <input v-model="filters.email" type="email" class="filters__search-input"
          placeholder="Search by company email..." />
      </div>

      <!-- Status (of supplier types — approval_status from pivot) -->
      <div class="filters__field">
        <label class="filters__label">Status</label>
        <div class="filters__select-wrap">
          <select v-model="filters.status" class="filters__select">
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      <!-- Business type -->
      <div class="filters__field">
        <label class="filters__label">Business Type</label>
        <div class="filters__select-wrap">
          <select v-model="filters.business_type" class="filters__select">
            <option value="supplier">Supplier</option>
            <option value="manufacturer">Manufacturer</option>
            <option value="service_provider">Service Provider</option>
          </select>
        </div>
      </div>
    </FiltersForm>

    <div class="card p-1 bg-white">
      <LoadingBar v-if="loading" message="Loading Suppliers..." />

      <div v-else class="table-wrapper">
        <ReusableTable ref="tableRef" :rows="suppliers" row-key="id" :selectable="true" :actions="true"
          empty-text="No members found" @bulk-action="handleBulkAction" @update:selected="onSelectionChange">

          <template #header>
            <th @click="sortBy('company_name')" class="sortable" style="width: 27%">
              Company <i :class="getSortIcon('company_name')"></i>
            </th>
            <th @click="sortBy('email')" class="sortable" style="width: 20%">
              Contact <i :class="getSortIcon('email')"></i>
            </th>
            <th @click="sortBy('status')" class="sortable" style="width: 25%">
              Status <i :class="getSortIcon('status')"></i>
            </th>
            <th @click="sortBy('created_at')" class="sortable" style="width: 16%">
              Registered On <i :class="getSortIcon('created_at')"></i>
            </th>
          </template>

          <template #row="{ row: supplier }">
            <td class="align-middle">
              <div class="d-flex align-items-center gap-2">
                <div
                  class="d-flex align-items-center justify-content-center bg-primary-subtle border border-primary-subtle rounded-3"
                  style="width: 30px; height: 30px; min-width: 30px;">
                  <span class="material-symbols-outlined text-primary fs-10">domain</span>
                </div>
                <span class="text-dark">{{ supplier.company_name }}</span>
              </div>
            </td>
            <td>
              <div class="d-flex flex-column">
                <span class="text-dark">{{ supplier.email }}</span>
                <span class="text-muted fs-11">{{ supplier.phone }}</span>
              </div>
            </td>
            <td>
              <ItemsDropdown :items="displayTypes(supplier)" label="Types" header-title="Business Types" name-key="name"
                status-key="approval_status" icon="tags" row-icon="tag" />
            </td>
            <td>{{ supplier.created_at }}</td>
          </template>

         <template #menu="{ row: supplier, close }">
  <li v-if="supplier.approval_submitted == true">
    <a class="dropdown-item" @click.prevent="openReviewModal(supplier); close()">
      <i class="fa fa-clipboard-check text-warning"></i>
      <span>Review</span>
    </a>
  </li>
  <!-- Show "Waiting for submission" text if not submitted -->
  <li v-else>
    <a class="dropdown-item disabled" style="cursor: default; opacity: 0.6;">
      <i class="fa fa-clock-o text-muted"></i>
      <span>Waiting for submission</span>
    </a>
  </li>
  <li>
    <a class="dropdown-item" @click.prevent="viewDetails(supplier); close()">
      <i class="fa fa-eye text-info"></i>
      <span>Profile</span>
    </a>
  </li>
  <!-- <li>
    <a class="dropdown-item" @click.prevent="edit(supplier); close()">
      <i class="fa fa-pencil text-primary"></i>
      <span>Edit</span>
    </a>
  </li>
  <hr class="dropdown-divider" />
  <li>
    <a class="dropdown-item text-danger" @click.prevent="remove(supplier); close()">
      <i class="fa fa-trash text-danger"></i>
      <span>Delete</span>
    </a>
  </li> -->
</template>
        </ReusableTable>
      </div>

      <Pagination :pagination="pagination" @page-change="changePage" />
    </div>
  
    <FormModal ref="reviewModal" title="Review Supplier Types" id="review_supplier_types_modal">
      <div v-if="reviewingSupplier">
        <p class="rform__help" style="margin-bottom: 14px;">
          Review <strong>{{ reviewingSupplier.company_name }}</strong>'s submitted business types one at a time.
        </p>
 
        <div class="rform__field">
          <label class="rform__label">Business Type</label>
          <div class="rform__select-wrap">
            <select v-model="selectedTypeId" class="rform__select" @change="resetDraft">
              <option v-for="t in reviewingSupplier.supplier_types" :key="t.id" :value="t.id">
                {{ formatBusinessType(t.name) }}
              </option>
            </select>
          </div>
        </div>
 
        <div v-if="selectedType" class="review-current-status">
          <span class="review-current-status__label">Current status:</span>
          <span v-html="getStatusBadge(selectedType.approval_status)" class="mt-3"></span>
        </div>
 
        <div class="rform__field">
          <label class="rform__label rform__label--required">New Status</label>
          <div class="rform__select-wrap">
            <select v-model="statusDraft.status" class="rform__select">
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="partially_approved">Partially Approved</option>
            </select>
          </div>
        </div>
 
        <div v-if="statusDraft.status !== 'approved'" class="rform__field">
          <label class="rform__label rform__label--required">Remarks</label>
          <textarea
            v-model="statusDraft.remarks"
            class="rform__textarea"
            rows="3"
            placeholder="Explain the reason for this status..."
            :class="{ 'is-invalid': remarksError }"
          ></textarea>
          <span v-if="remarksError" class="rform__error">{{ remarksError }}</span>
        </div>
 
        <div class="rform__row" style="justify-content: flex-end; margin-top: 16px;">
          <button type="button" class="rform__btn rform__btn--ghost" @click="closeReviewModal('reviewModal')">Close</button>
          <button type="button" class="rform__btn rform__btn--primary" :disabled="saving" @click="saveTypeStatus">
            {{ saving ? 'Saving…' : 'Save Status' }}
          </button>
        </div>
      </div>
    </FormModal>
    <ConfirmDeleteModal v-model="showDeleteModal" title="Delete Supplier" :item-name="deletingSupplier?.company_name"
      :loading="deleting" @confirm="confirmDelete" />
  </div>
</template>

<script>
import Pagination from '@/components/utils/PaginationBar.vue'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import ReusableTable from '@/components/utils/ListTable.vue'
import FiltersForm from '@/components/utils/FiltersForm.vue'
import FormModal from '@/components/utils/FormModal.vue'
import ConfirmDeleteModal from '@/components/utils/ConfirmDeleteModal.vue'
import { form_api_submit } from '@/utils/FormSubmit'
import { getStatusBadge } from '@/utils/getStatusBadge'
import ItemsDropdown from '@/components/AttributesDropdown.vue'
export default {
  name: 'AllSuppliers',
  components: {
    Pagination,
    LoadingBar,
    ReusableTable,
    FiltersForm,
    FormModal,
    ConfirmDeleteModal,
    ItemsDropdown,
  },
  data() {
    return {
      suppliers: [],
      selectedIds: [],
      loading: false,

      sortColumn: null,
      sortOrder: 'asc',

      filters: {
        name: '',
        status: 'all',
        email: '',
        business_type: 'all',
      },
      currentFilters: {},

      pagination: {
        current_page: 1,
        per_page: 20,
        total_rows: 0,
        total_pages: 1,
      },

      reviewingSupplier: null,
      savingTypeId: null,
   
      selectedTypeId: null, 
      statusDraft: { status: 'pending', remarks: '' },
      remarksError: '',
      saving: false,
      // Delete flow
      showDeleteModal: false,
      deletingSupplier: null,
      deleting: false,

      approvingId: null,
    }
  },
  mounted() {
    this.fetchList()
  },
  computed: {
  selectedType() {
    if (!this.reviewingSupplier || !this.selectedTypeId) return null
    return (this.reviewingSupplier.supplier_types || []).find((t) => t.id === this.selectedTypeId) || null
  },
},
  methods: {
    getStatusBadge,
    displayTypes(supplier) {
      return (supplier.supplier_types || []).map((t) => ({
        ...t,
        name: this.formatBusinessType(t.name),
      }))
    },

    async fetchList() {
      this.loading = true

      const result = await form_api_submit({
        url: 'supplier/list',
        method: 'GET',
        data: {
          page: this.pagination.current_page,
          sort_column: this.sortColumn,
          sort_order: this.sortOrder,
          ...this.currentFilters,
        },
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false,
      })

      if (result.success) {
        // API currently returns a flat array in `data`, not { list, pagination }.
        const rows = Array.isArray(result.data) ? result.data : (result.data.list || [])

        this.suppliers = rows.map((s) => ({
          ...s,
          _dropdownOpen: false,
          status: this.normalizeStatus(s.approval_status),
        }))
        console.log(this.suppliers);
        if (result.data.pagination) {
          this.pagination = { ...this.pagination, ...result.data.pagination }
        } else {
          // No pagination block from the API yet — fall back to client-side count.
          this.pagination.total_rows = this.suppliers.length
          this.pagination.total_pages = 1
        }
      }

      this.loading = false
    },

    normalizeStatus(approvalStatus) {
      const map = {
        in_progress: 'pending',
        approved: 'approved',
        rejected: 'rejected',
      }
      return map[approvalStatus] || approvalStatus || 'pending'
    },
    onApply(filters) {
      this.currentFilters = { ...filters }
      this.pagination.current_page = 1
      this.fetchList()
    },
    refresh() {
      this.filters = { name: '', email: '', status: 'all', business_type: 'all' }
      this.currentFilters = {}
      this.pagination.current_page = 1
      this.fetchList()
    },
    changePage(page) {
      if (page < 1 || page > this.pagination.total_pages) return
      this.pagination.current_page = page
      this.fetchList()
    },

    // ---- Sorting (server-driven — re-fetches with new sort params) ----
    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = column
        this.sortOrder = 'asc'
      }
      this.fetchList()
    },
    getSortIcon(column) {
      if (this.sortColumn !== column) return 'fas fa-sort text-muted'
      return this.sortOrder === 'asc' ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary'
    },

    // ---- Row / bulk actions ----
    onSelectionChange(ids) {
      this.selectedIds = ids
    },
    handleBulkAction({ action, ids }) {
      if (action === 'export') {
        alert(`Exporting ${ids.length} members(s)`)
        return
      }
      if (action === 'delete') {
        alert(`Deleting ${ids.length} members(s)`)
        this.$refs.tableRef?.clearSelection()
        this.fetchList()
      }
    },

    viewDetails(supplier) {
      this.$router.push({ name: 'AdminSupplierProfile', params: { id: supplier.external_id } })
    },
    edit(supplier) {
      this.$router.push({ name: 'SupplierEdit', params: { id: supplier.id } })
    },
    remove(supplier) {
      this.deletingSupplier = supplier
      this.showDeleteModal = true
    },
    async confirmDelete() {
      this.deleting = true
      const result = await form_api_submit({
        url: `suppliers/${this.deletingSupplier.id}`,
        method: 'DELETE',
        show_success_toast: true,
        show_error_toast: true,
      })
      this.deleting = false
      this.showDeleteModal = false
      this.deletingSupplier = null
      if (result.success) this.fetchList()
    },
   openReviewModal(supplier) {
     this.reviewingSupplier = supplier
      this.selectedTypeId = supplier.supplier_types?.[0]?.id ?? null
      this.resetDraft()
      this.$refs.reviewModal.showModal()
    },
          resetDraft() {
          this.statusDraft = {
            status: this.selectedType?.approval_status || 'pending',
            remarks: '',
          }
          this.remarksError = ''
        },
        closeReviewModal(refName) {
          this.$refs[refName].closeModal()
          this.reviewingSupplier = null
          this.rejectingTypeId = null
        },
        async saveTypeStatus() {
          if (!this.selectedType) return

          if (!this.reviewingSupplier?.id) {
            console.error('reviewingSupplier has no id:', this.reviewingSupplier)
            this.remarksError = 'Something went wrong identifying this supplier. Please reopen the review modal.'
            return
          }

          if (this.statusDraft.status !== 'approved' && !this.statusDraft.remarks.trim()) {
            this.remarksError = 'Remarks are required for this status.'
            return
          }
          this.remarksError = ''
          this.saving = true

          const payload = {
            supplier_id: this.reviewingSupplier.id,
            type_id: this.selectedType.id,
            approval_status: this.statusDraft.status,
          }
          if (this.statusDraft.status !== 'approved') {
        payload.remarks = this.statusDraft.remarks
      }
      const result = await form_api_submit({
        url: 'supplier/approve',
        method: 'POST',
        data: payload,
        show_success_toast: true,
        show_error_toast: true,
      })

      this.saving = false

      if (result.success) {
        this.selectedType.approval_status = this.statusDraft.status
        this.resetDraft()
        this.closeReviewModal('reviewModal')
        this.fetchList()
      }
    },
        formatBusinessType(type) {
      const types = {
        supplier: 'Supplier',
        manufacturer: 'Manufacturer',
        service_provider: 'Service Provider',
      }
      return types[type] || type
    },
    formatStatus(status) {
      const statuses = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' }
      return statuses[status] || status
    },
  },
}
</script>

<style scoped>
.table-wrapper table {
  width: 100%;
  table-layout: fixed;
}

/* Approve / Reject buttons */
.btn-review {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s ease, opacity 0.15s ease;
}

.btn-review:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-review--approve {
  background: rgba(26, 127, 75, 0.1);
  color: #1a7f4b;
  border-color: rgba(26, 127, 75, 0.25);
}

.btn-review--approve:hover:not(:disabled) {
  background: rgba(26, 127, 75, 0.18);
}

.btn-review--reject {
  background: rgba(179, 38, 30, 0.06);
  color: #b3261e;
  border-color: rgba(179, 38, 30, 0.22);
}

.btn-review--reject:hover {
  background: rgba(179, 38, 30, 0.12);
}

.review-type-row {
  padding: 12px 0;
  border-bottom: 1px solid var(--color-border);
}

.review-type-row:last-child {
  border-bottom: none;
}

.review-type-row__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.review-type-row__info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.review-type-row__actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.review-type-row__remarks {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed var(--color-border);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.review-type-row__remarks-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.btn-review--review {
  background: rgba(11, 23, 153, 0.06);
  color: var(--color-primary, #0b1799);
  border-color: rgba(11, 23, 153, 0.2);
}

.btn-review--review:hover {
  background: rgba(11, 23, 153, 0.12);
}
.review-type-row__select {
  min-width: 140px;
}
 
.rform__btn--sm {
  height: 34px;
  padding: 0 14px;
  font-size: 12.5px;
}
.review-current-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: -6px 0 14px;
  font-size: 12.5px;
}
.review-current-status__label {
  color: var(--color-text-muted);
  margin-top: 10px;
  font-weight: 600;
}
 
</style>